import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Database, Layers, Globe, Code, TrendingUp, Shield, Construction, FileText, Smartphone, Server } from 'lucide-react';
import SEO from '../components/SEO';
import './Products.css';

const PRODUCTS = [
  {
    id: 'rta',
    icon: <Database size={32} />,
    tag: 'Finance',
    name: 'Registrar & Transfer Agent (RTA) Software',
    tagline: 'Effortless · Accurate · Secure',
    desc: 'An end-to-end platform for Registrar & Transfer Agents handling mutual fund and corporate securities operations. Fully compliant with SEBI guidelines.',
    features: [
      'End-to-end shareholder record management',
      'Securities transfer processing automation',
      'Dividend distribution & tax reporting',
      'SEBI & AMFI compliance built-in',
      'Investor grievance management portal',
      'Bulk processing for large folios',
      'RESTful API for depository integration',
      'Audit trails & compliance reporting',
    ],
    clients: ['Religare', 'SKI', 'Mindex'],
    color: '#C0392B',
  },
  {
    id: 'school',
    icon: <Layers size={32} />,
    tag: 'Education',
    name: 'School Management ERP',
    tagline: 'Smart · Seamless · Student-Centric',
    desc: 'Complete school management software covering admissions through alumni — connecting students, parents, teachers, and administration on a single platform.',
    features: [
      'Smart admissions & enrollment workflow',
      'Academic scheduling & timetabling',
      'Online & offline fee collection',
      'Attendance with biometric/RFID support',
      'Parent-teacher communication portal',
      'Exam management & report cards',
      'Real-time dashboards & analytics',
      'Mobile apps for parents & students',
    ],
    clients: ['IWP Academy', 'Green Land Model School'],
    color: '#8B0000',
  },
  {
    id: 'hotel',
    icon: <Globe size={32} />,
    tag: 'Hospitality',
    name: 'Hotel Management Software',
    tagline: 'Elevate Every Guest Experience',
    desc: 'A comprehensive Property Management System for hotels of all sizes — from boutique properties to multi-chain operations.',
    features: [
      'Reservation & channel management',
      'Front desk & check-in/check-out',
      'Housekeeping task management',
      'Restaurant & POS integration',
      'Revenue management & forecasting',
      'Loyalty & CRM for guests',
      'OTA integrations (Booking.com, Expedia)',
      'Finance & night audit automation',
    ],
    clients: ['Valorelle', 'Indus Hotels'],
    color: '#6B0000',
  },
  {
    id: 'ecommerce',
    icon: <Code size={32} />,
    tag: 'Retail',
    name: 'E-Commerce Platform',
    tagline: 'Empowering Scalable Businesses',
    desc: 'A full-stack e-commerce solution covering storefront, inventory, payments and customer experience for startups to enterprise retailers.',
    features: [
      'Multi-vendor marketplace capability',
      'Product catalog & inventory management',
      'Payment gateway integration (Razorpay, Paytm, UPI)',
      'Order management & logistics tracking',
      'Customer reviews & loyalty points',
      'Abandoned cart recovery automation',
      'SEO-optimized storefront builder',
      'Mobile-first PWA storefront',
    ],
    clients: ['Sparepedia', 'Abhipra'],
    color: '#C0392B',
  },
  {
    id: 'erp',
    icon: <TrendingUp size={32} />,
    tag: 'Enterprise',
    name: 'CRM & ERP Suite',
    tagline: 'Integrated · Scalable · Efficient',
    desc: 'Enterprise-grade CRM and ERP modules that centralize your finance, HR, inventory, sales pipeline and operations into a single source of truth.',
    features: [
      'Lead, deal & pipeline management',
      'Customer 360° profile & history',
      'Inventory & warehouse management',
      'Finance, invoicing & GST compliance',
      'HR, payroll & attendance',
      'Procurement & vendor management',
      'Business intelligence dashboards',
      'Custom workflow automation engine',
    ],
    clients: ['Maashitla', 'Sports University'],
    color: '#8B0000',
  },
  {
    id: 'scaffold',
    icon: <Shield size={32} />,
    tag: 'Construction',
    name: 'Scaffolding Solutions',
    tagline: 'Efficient · Customizable · Secure',
    desc: 'End-to-end scaffolding management platform for construction and industrial projects — from material planning to safety compliance tracking.',
    features: [
      'Project-wise scaffold planning & BOM',
      'Material procurement & inventory',
      'Erection & dismantling scheduling',
      'Safety inspection checklists',
      'On-site worker management',
      'Progress photo documentation',
      'Client billing & material utilization',
      'OSHA compliance reports',
    ],
    clients: [],
    color: '#6B0000',
  },
  {
    id: 'docs',
    icon: <FileText size={32} />,
    tag: 'Services',
    name: 'Documentation Services',
    tagline: 'Organized · Accurate · Reliable',
    desc: 'Professional document digitization, creation, and management services for legal, compliance, and operational documentation.',
    features: [
      'Document digitization & OCR',
      'Compliance document management',
      'Legal document templates',
      'Version control & access management',
      'Digital signature integration',
      'Audit-ready document workflows',
    ],
    clients: [],
    color: '#C0392B',
  },
  {
    id: 'mobile',
    icon: <Smartphone size={32} />,
    tag: 'Mobile',
    name: 'Custom Mobile Apps',
    tagline: 'Android · iOS · Cross-Platform',
    desc: 'Native and cross-platform mobile applications built to complement your SHIVKMS software or as standalone solutions.',
    features: [
      'iOS & Android native development',
      'React Native cross-platform apps',
      'Offline-first architecture',
      'Push notifications & real-time sync',
      'Biometric authentication',
      'Integration with all SHIVKMS products',
    ],
    clients: [],
    color: '#8B0000',
  },
];

const TAGS = ['All', 'Finance', 'Education', 'Hospitality', 'Retail', 'Enterprise', 'Construction', 'Services', 'Mobile'];

export default function Products() {
  const [activeTag, setActiveTag] = useState('All');
  const [activeProduct, setActiveProduct] = useState(null);

  const filtered = activeTag === 'All' ? PRODUCTS : PRODUCTS.filter(p => p.tag === activeTag);

  return (
    <div className="products-page">
      <SEO
        title="Software Products & Services — RTA, ERP, School, Hotel & More"
        description="Explore all 8 SHIVKMS products: RTA Software, School Management ERP, Hotel Management, E-Commerce, CRM & ERP Suite, Scaffolding Solutions, Documentation Services and Mobile Apps."
        keywords="RTA software, school management software, hotel management software, e-commerce platform, CRM ERP software, scaffolding software, mobile app development India"
        path="/products"
      />
      {/* Hero */}
      <section className="products-hero">
        <div className="products-hero-bg" />
        <div className="container">
          <div className="products-hero-inner">
            <span className="eyebrow">Product Suite</span>
            <h1>Software for Every <span className="gradient-text">Business Challenge</span></h1>
            <p>8 flagship products. 1 trusted partner. Modular by design so you only pay for what you need.</p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <div className="products-filter-bar">
        <div className="container">
          <div className="filter-tags">
            {TAGS.map(tag => (
              <button
                key={tag}
                className={`filter-tag ${activeTag === tag ? 'active' : ''}`}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <section className="section-py">
        <div className="container">
          <div className="products-full-grid">
            {filtered.map((p, i) => (
              <div key={p.id} className="product-full-card card">
                <div className="pfc-header">
                  <div className="pfc-icon" style={{ background: `${p.color}20`, border: `1px solid ${p.color}40`, color: p.color }}>
                    {p.icon}
                  </div>
                  <div>
                    <span className="pfc-tag">{p.tag}</span>
                    <h3 className="pfc-name">{p.name}</h3>
                    <span className="pfc-tagline">{p.tagline}</span>
                  </div>
                </div>
                <p className="pfc-desc">{p.desc}</p>
                <div className="pfc-features">
                  {p.features.slice(0, 6).map((f, j) => (
                    <div key={j} className="pfc-feature">
                      <CheckCircle size={14} className="pfc-check" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                {p.clients.length > 0 && (
                  <div className="pfc-clients">
                    <span className="pfc-clients-label">Used by:</span>
                    {p.clients.map(c => (
                      <span key={c} className="pfc-client-badge">{c}</span>
                    ))}
                  </div>
                )}
                <Link to="/contact" className="pfc-cta btn-primary">
                  Request Demo <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Banner */}
      <section className="tech-stack-banner">
        <div className="tsb-bg" />
        <div className="container">
          <div className="tsb-inner">
            <div className="tsb-text">
              <h2>Built on Modern <span className="gradient-text">Technology</span></h2>
              <p>Every SHIVKMS product is architected for performance, security, and scale.</p>
            </div>
            <div className="tsb-chips">
              {['React', 'Node.js', '.NET Core', 'Java Spring', 'PostgreSQL', 'Redis', 'AWS / Azure', 'Docker', 'REST APIs', 'React Native'].map(t => (
                <span key={t} className="tech-chip">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-py" style={{textAlign:'center', background:'var(--bg-base)'}}>
        <div className="container">
          <h2>Not sure which product fits?</h2>
          <p style={{color:'var(--text-secondary)', margin:'16px auto 32px', maxWidth:'500px'}}>
            Book a free 30-minute consultation and our team will map your workflows to the right solution.
          </p>
          <Link to="/contact" className="btn-primary">
            Talk to an Expert <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
